import { InjectionToken } from '@angular/core';

/**
 * App-supplied delegate for the parts of an auth lifecycle the shell deliberately
 * does NOT own: INITIATING a login (e.g. redirect to B2C) and performing a LOGOUT.
 *
 * The shell {@link AuthService} is a SESSION-PROBE only (status / refresh / me /
 * roles against an already-established cookie session). Login-initiation and logout
 * are portal-specific (B2C / PKCE flows, return-url handling, sign-out endpoints),
 * so the app provides them here. Both methods are optional: an app may supply only
 * one, or none (probe-only).
 *
 * Tied to PBI 1466 (dedicated Phoenix B2C app-registration). The shell does NOT
 * implement B2C/PKCE itself.
 */
export interface AuthSessionGateway {
  /** Begin an interactive login (e.g. redirect to B2C). `returnUrl` is where to land after. */
  initiateLogin?(returnUrl?: string): void;
  /** End the session (sign-out + local cleanup). */
  logout?(): void;
}

/**
 * Optional DI token for an {@link AuthSessionGateway}. If unprovided, the shell
 * {@link AuthService}'s `initiateLogin`/`logout` pass-throughs are a documented
 * no-op (with a `console.warn`) — the shell instance is session-probe-only.
 */
export const AUTH_SESSION_GATEWAY = new InjectionToken<AuthSessionGateway>(
  'AUTH_SESSION_GATEWAY'
);
